/**
 * Server-side HA features (phase B): alerts, schedule editing, config tree,
 * admin registry dump. Additive to protocol.ts / model.ts.
 */
import { z } from 'zod';
import { ModuleId } from './model.js';
// ---------- alerts (WS server → client: AlertsMsg) ----------
export const AlertSeverity = z.enum(['info', 'warning', 'critical']);
/** One thing behind a grouped alert (devices_offline: each device, its room / integration and since when). */
export const AlertItem = z.object({
    name: z.string(),
    room: z.string().nullable().optional(),
    integration: z.string().optional(),
    /** Epoch seconds. */
    since: z.number(),
});
export const Alert = z.object({
    /** Stable while the condition persists: `${ruleId}` or `${ruleId}:${entityId}`. */
    id: z.string(),
    severity: AlertSeverity,
    title: z.string(),
    detail: z.string(),
    entityId: z.string().optional(),
    /** Epoch seconds the condition started. */
    since: z.number(),
    /** Dashboard the alert belongs to (server-derived from module membership of its entity). */
    module: ModuleId.optional(),
    /** Only sent to home admins (rule flag). */
    adminOnly: z.boolean().optional(),
    /** Grouped alerts: what's behind it, for the expanded views. */
    items: z.array(AlertItem).optional(),
});
// ---------- schedule helpers (REST /api/homes/:home/schedules/:entityId) ----------
/** "HH:MM" or "HH:MM:SS"; `to` may be "24:00:00". */
const Time = z.string().regex(/^([01]\d|2[0-4]):[0-5]\d(:[0-5]\d)?$/);
export const ScheduleBlock = z.object({ from: Time, to: Time, data: z.record(z.string(), z.unknown()).optional() });
export const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
/** GET response / PUT body. PUT replaces every day given; omitted days are kept. */
export const ScheduleDefinition = z.object({
    name: z.string().min(1).optional(),
    icon: z.string().nullable().optional(),
    monday: z.array(ScheduleBlock).optional(),
    tuesday: z.array(ScheduleBlock).optional(),
    wednesday: z.array(ScheduleBlock).optional(),
    thursday: z.array(ScheduleBlock).optional(),
    friday: z.array(ScheduleBlock).optional(),
    saturday: z.array(ScheduleBlock).optional(),
    sunday: z.array(ScheduleBlock).optional(),
});
// ---------- config tree (GET /api/homes/:home/config-tree) ----------
export const ConfigTier = z.enum(['everyday', 'advanced', 'grid-protection']);
//# sourceMappingURL=ha.js.map